import { ALL_ASSET_TYPES, type AssetType, type ProcessingState } from '../../types';
import type { ViewMode } from './mediaLibraryTypes';
import { INITIAL_ASSET_QUERY, type AssetQuery } from './mediaLibraryTypes.AssetQuery';

type AssetSort = AssetQuery['sort'];

/**
 * What the listing is showing: which folder, found how, laid out how.
 *
 * Plain data with no idea where it is kept. The library holds it in memory by default, and a host
 * can keep it somewhere of its own -- its URL -- through `AssetListingStore`. Every change to it is
 * one of the `with*` transitions below, so the rules are the same whichever store is behind it.
 */
export type AssetListingState = {
  viewMode: ViewMode;
  /**
   * The folder somebody opened, or `null` when nobody has yet, which reads as the root.
   * `ROOT_FOLDER_ID` is the root, opened on purpose.
   */
  activeFolderId: string | null;
  /** A non-empty search spans the library below the open folder. Debounced by the toolbar. */
  search: string;
  /** Empty means "every type the mount allows". */
  types: readonly AssetType[];
  states: readonly ProcessingState[];
  sort: AssetSort;
};

export const INITIAL_ASSET_LISTING_STATE: AssetListingState = {
  viewMode: 'grid',
  activeFolderId: null,
  search: '',
  types: [],
  states: [],
  sort: INITIAL_ASSET_QUERY.sort,
};

/*
 * Transitions: the use cases of the listing. Each returns the next state and shares every field it
 * did not touch by reference, so a store can tell a no-op by identity.
 */
export const withViewMode = (state: AssetListingState, viewMode: ViewMode): AssetListingState =>
  state.viewMode === viewMode ? state : { ...state, viewMode };

/** Clears the search, so the folder does not open filtered by a term typed somewhere else. */
export const withFolderOpened = (state: AssetListingState, folderId: string): AssetListingState => ({
  ...state,
  activeFolderId: folderId,
  search: '',
});

export const withSearch = (state: AssetListingState, search: string): AssetListingState =>
  state.search === search ? state : { ...state, search };

/** Adds `type` to the type filter, or removes it if it is already there. */
export const withTypeToggled = (state: AssetListingState, type: AssetType): AssetListingState => ({
  ...state,
  types: state.types.includes(type) ? state.types.filter((t) => t !== type) : [...state.types, type],
});

export const withSort = (state: AssetListingState, sort: AssetSort): AssetListingState => ({ ...state, sort });

/** Search, filters and sort back to their defaults; the folder and the layout stay. */
export const withQueryReset = (state: AssetListingState): AssetListingState => ({
  ...state,
  search: '',
  types: [],
  states: [],
  sort: INITIAL_ASSET_QUERY.sort,
});

/*
 * Reads: what the rest of the library is given, with the mount's configuration filled in.
 */

/**
 * The query the server is asked, which is also what the assets cache is keyed on.
 *
 * A restricted mount only ever narrows within its own types: whatever the store holds -- and a
 * host's store may hold a hand-edited URL -- cannot widen a picker past what it was opened for.
 * It always names its types, because an empty `types` means every type on the wire.
 */
export const toAssetQuery = (
  state: Pick<AssetListingState, 'search' | 'types' | 'states' | 'sort'>,
  allowedTypes: readonly AssetType[]
): AssetQuery => {
  const chosen = state.types.filter((type) => allowedTypes.includes(type));
  const isRestricted = allowedTypes.length < ALL_ASSET_TYPES.length;

  return {
    search: state.search,
    types: chosen.length === 0 && isRestricted ? allowedTypes : chosen,
    states: state.states,
    sort: state.sort,
  };
};
